import { useRef } from "react";
import { gsap, useGSAP, MOTION_OK } from "../gsap";
import { CtaButton } from "./CtaButton";
import { heroImage } from "../content";

export function Hero({ ready }: { ready: boolean }) {
  const root = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      // Wait for the loader to start opening, so the entrance is seen.
      if (!ready) return;
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        // Entry: the name lands first, then the piece, then the way in.
        gsap
          .timeline({ defaults: { ease: "expo.out" }, delay: 0.35 })
          .from(".hero-line", { yPercent: 115, duration: 1.4, stagger: 0.12 })
          .from(".hero-img", { clipPath: "inset(100% 0% 0% 0%)", duration: 1.4 }, 0.25)
          .from(".hero-fade", { opacity: 0, y: 24, duration: 1, stagger: 0.1 }, 0.7);

        // Depth on exit: the image leaves faster than the headline.
        gsap.to(".hero-drift", {
          yPercent: -22,
          ease: "none",
          scrollTrigger: { trigger: root.current, start: "top top", end: "bottom top", scrub: true },
        });
      });
    },
    { scope: root, dependencies: [ready] },
  );

  return (
    <section
      ref={root}
      id="top"
      className="relative flex min-h-[100dvh] flex-col justify-end px-4 pb-6 pt-24 md:px-8 md:pb-8"
    >
      <p className="hero-fade mb-8 text-[11px] font-medium uppercase tracking-[0.2em] text-smoke md:absolute md:left-8 md:top-28 md:mb-0">
        Digital art and handcrafted objects
      </p>

      <div className="hero-drift relative mb-6 ml-auto w-4/5 md:absolute md:left-[32%] md:top-[12dvh] md:mb-0 md:w-[min(32vw,56dvh)]">
        {/* Round double-bezel: the logo is a disc, so the frame follows it. */}
        <div
          className="hero-img rounded-full bg-white/[0.04] p-2 ring-1 ring-white/10"
          style={{ clipPath: "inset(0% 0% 0% 0%)" }}
        >
          <img
            src={heroImage.src}
            alt={heroImage.title}
            width={heroImage.width}
            height={heroImage.height}
            fetchPriority="high"
            decoding="async"
            className="block aspect-square h-auto w-full rounded-full"
          />
        </div>
      </div>

      <div className="relative">
        <h1
          aria-label="Miss Stabbypants"
          className="pointer-events-none relative z-10 font-display text-[13.5vw] font-extrabold uppercase leading-[0.86] tracking-[-0.045em] mix-blend-difference md:text-[8.1vw]"
        >
          <span aria-hidden className="block overflow-hidden pt-[0.06em]">
            <span className="hero-line block">Miss</span>
          </span>
          {/* One word on desktop, split over two lines on mobile so it stays large. */}
          <span aria-hidden className="block md:flex">
            <span className="block overflow-hidden pt-[0.06em]">
              <span className="hero-line block">Stabby</span>
            </span>
            <span className="block overflow-hidden pt-[0.06em]">
              <span className="hero-line block">pants</span>
            </span>
          </span>
        </h1>

        <div className="relative z-20 mt-8 flex flex-col gap-6 md:absolute md:right-0 md:top-0 md:mt-0 md:h-[7.45vw] md:w-[24rem] md:justify-end">
          <p className="hero-fade max-w-[38ch] text-base leading-relaxed text-smoke">
            Dark, sharp and made with care. Class crests on screen. Leather, chainmail and dice goods by hand.
          </p>
          <div className="hero-fade flex flex-wrap items-center gap-5">
            <CtaButton />
            <a
              href="#digital"
              className="inline-flex min-h-11 items-center text-sm underline decoration-white/30 underline-offset-8 transition-colors duration-500 ease-heavy hover:decoration-stab"
            >
              See the work
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
